import React from 'react';
import { STORE_EMAIL } from '../constants/contact';

const COMPANY_LEGAL_NAME = '[RAZÃO SOCIAL]';
const COMPANY_CNPJ = '[CNPJ]';
const PRIVACY_CONTACT_EMAIL = '[E-MAIL DE CONTATO PARA PRIVACIDADE]';
const LAST_UPDATED = '[DATA DA ÚLTIMA ATUALIZAÇÃO]';

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-bold text-text-dark">{title}</h2>
      <div className="flex flex-col gap-3 text-sm text-text-medium leading-relaxed">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-5 flex flex-col gap-1.5">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function PrivacyPolicyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
      <h1 className="font-brand text-3xl sm:text-4xl text-chocolate leading-tight">Política de privacidade</h1>
      <p className="text-xs text-text-light mt-2">Última atualização: {LAST_UPDATED}</p>

      <div className="mt-6 rounded-2xl border border-champagne bg-white shadow-sm p-5 sm:p-8 flex flex-col gap-8">
        <Section title="1. Quem somos">
          <p>
            Esta política explica como a Baby Chic, operada por {COMPANY_LEGAL_NAME}, inscrita no CNPJ{' '}
            {COMPANY_CNPJ} ("nós"), trata os dados pessoais de quem visita e compra em nossa loja virtual, em
            conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 - LGPD). Atendemos clientes em todo
            o Brasil.
          </p>
        </Section>

        <Section title="2. Dados que coletamos">
          <BulletList
            items={[
              'Cadastro: nome, e-mail, telefone, CPF (quando necessário), data de nascimento e senha (armazenada de forma protegida).',
              'Pedidos: itens comprados, endereço de entrega, forma de pagamento escolhida e histórico de compras.',
              'Contato: mensagens e informações enviadas a nós por WhatsApp, e-mail ou redes sociais.',
              'Navegação: páginas visitadas, produtos visualizados, itens adicionados à sacola, tipo de dispositivo, navegador e endereço IP, coletados por meio de cookies e tecnologias semelhantes, somente com o seu consentimento.',
            ]}
          />
        </Section>

        <Section title="3. Para que usamos seus dados">
          <BulletList
            items={[
              'Processar, entregar e acompanhar seus pedidos e pagamentos.',
              'Prestar atendimento, inclusive via WhatsApp, e tratar trocas e devoluções.',
              'Manter sua conta e preferências, como favoritos e endereços.',
              'Entender como a loja é usada e melhorar a experiência de navegação e compra.',
              'Medir e melhorar a eficácia de nossos anúncios e exibir anúncios relevantes.',
              'Cumprir obrigações legais e regulatórias e exercer direitos em processos.',
            ]}
          />
          <p>
            As bases legais incluem a execução de contrato, o cumprimento de obrigação legal, o legítimo interesse e,
            para cookies de análise e marketing, o seu consentimento.
          </p>
        </Section>

        <Section title="4. Cookies e ferramentas de terceiros">
          <p>
            Usamos cookies e scripts de terceiros apenas depois que você aceita no aviso de cookies. Você pode
            recusar sem prejuízo ao uso da loja e alterar sua escolha a qualquer momento em "Preferências de cookies",
            no rodapé do site.
          </p>
          <BulletList
            items={[
              'Meta Pixel (Meta Platforms): mede conversões e permite exibir anúncios no Facebook e Instagram. Ativado somente com o consentimento de marketing.',
              'Microsoft Clarity (Microsoft): registra mapas de calor e gravações anônimas de sessões para entendermos como a loja é usada. Campos de formulário são mascarados e não enviamos nome, e-mail ou telefone. Ativado somente com o consentimento de análise.',
            ]}
          />
          <p>
            Essas empresas podem tratar dados conforme suas próprias políticas de privacidade e, em alguns casos,
            em servidores localizados fora do Brasil.
          </p>
        </Section>

        <Section title="5. Compartilhamento de dados">
          <p>Não vendemos seus dados. Podemos compartilhá-los apenas com:</p>
          <BulletList
            items={[
              'Transportadoras e serviços de entrega, para realizar o envio dos pedidos.',
              'Provedores de pagamento e instituições financeiras, para processar suas compras.',
              'Fornecedores de tecnologia e hospedagem que nos ajudam a operar a loja.',
              'Ferramentas de análise e publicidade descritas acima, quando você consente.',
              'Autoridades públicas, quando exigido por lei ou ordem judicial.',
            ]}
          />
        </Section>

        <Section title="6. Por quanto tempo guardamos seus dados">
          <p>
            Mantemos seus dados pelo tempo necessário para cumprir as finalidades desta política e as obrigações
            legais, fiscais e contratuais aplicáveis. Dados de cadastro permanecem enquanto sua conta estiver ativa.
            Registros de pedidos e notas fiscais são guardados pelos prazos exigidos em lei. Após esse período, os
            dados são excluídos ou anonimizados.
          </p>
        </Section>

        <Section title="7. Seus direitos como titular">
          <p>Conforme o art. 18 da LGPD, você pode solicitar a qualquer momento:</p>
          <BulletList
            items={[
              'Confirmação de que tratamos seus dados e acesso a eles.',
              'Correção de dados incompletos, inexatos ou desatualizados.',
              'Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade.',
              'Portabilidade dos dados a outro fornecedor.',
              'Eliminação dos dados tratados com base no consentimento.',
              'Informação sobre com quem compartilhamos seus dados.',
              'Informação sobre a possibilidade de não consentir e suas consequências.',
              'Revogação do consentimento, a qualquer momento.',
            ]}
          />
          <p>
            Você também pode reclamar à Autoridade Nacional de Proteção de Dados (ANPD) caso entenda que seus
            direitos não foram respeitados.
          </p>
        </Section>

        <Section title="8. Segurança">
          <p>
            Adotamos medidas técnicas e administrativas para proteger seus dados contra acessos não autorizados,
            perda e alteração. Nenhum sistema é totalmente imune a riscos, mas trabalhamos para reduzi-los
            continuamente.
          </p>
        </Section>

        <Section title="9. Contato e encarregado">
          <p>
            Para exercer seus direitos ou tirar dúvidas sobre esta política, fale com {COMPANY_LEGAL_NAME} pelo
            e-mail {PRIVACY_CONTACT_EMAIL} ou pelos canais de atendimento da loja, como {STORE_EMAIL}.
          </p>
        </Section>

        <Section title="10. Alterações nesta política">
          <p>
            Podemos atualizar esta política periodicamente. Quando houver mudanças relevantes, a data de atualização
            no topo da página será revisada e, se necessário, solicitaremos novamente o seu consentimento.
          </p>
        </Section>
      </div>
    </div>
  );
}
